import { Box, IconButton } from '@mui/material';
import FileThumbnail, {
  FileThumbnailProps,
} from '@/components/file-thumbnail/FileThumbnail';
import React, { useEffect, useRef, useState } from 'react';
import { ExtendFile } from '@/components/file-thumbnail/utils';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { dashhed4 } from '@/theme/palette';
import { PlusIcon } from '@/theme/overrides/CustomIcons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css/free-mode';
import { Swiper as SwiperClass } from 'swiper/types'; // Swiper class import (중요)
import { createPortal } from 'react-dom';

export type MultiFileDragDropProps = {
  files: ExtendFile[];
  onDragStart: (e: DragStartEvent) => void;
  onDragEnd: (e: DragEndEvent) => void;
  onDragCancel: () => void;
  onRemove: (file: ExtendFile) => void;
  activeFile: ExtendFile | null;
  useAddButton: boolean;
  onFileOpen: () => void;
  sortable?: boolean;
} & Pick<
  FileThumbnailProps,
  'previewType' | 'onRemove' | 'disabled' | 'loading'
>;

export default function MultiFileDragDrop({
  files,
  onDragEnd,
  onDragStart,
  onDragCancel,
  onRemove,
  activeFile,
  previewType,
  disabled,
  loading,
  useAddButton,
  onFileOpen,
  sortable,
}: MultiFileDragDropProps) {
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null);
  const prevLenRef = useRef<number>(files.length);
  const imageSize = previewType === 'imageView_horizontal' ? 80 : 160;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 1 },
    }),
  );

  const isFileUploading = (file: ExtendFile) =>
    !file.isUploaded && file.status === 'new';

  useEffect(() => {
    if (swiperRef) {
      disabled ? swiperRef.disable() : swiperRef.enable();
    }
  }, [disabled, swiperRef]);

  useEffect(() => {
    if (previewType !== 'imageView_horizontal' || !swiperRef) return;

    const prev = prevLenRef.current;
    const curr = files.length;

    // 새 파일이 추가된 경우에만 오른쪽 끝으로 이동
    if (curr > prev) {
      // 새 슬라이드가 DOM에 반영된 뒤 이동하도록 약간 늦춰 호출
      requestAnimationFrame(() => {
        // 최신 상태 반영
        swiperRef.updateSlides();
        swiperRef.update();

        // 마지막 슬라이드로 이동 (애니메이션 300ms)
        swiperRef.slideTo(curr - 1, 300);
      });
    }

    prevLenRef.current = curr;
  }, [files.length, previewType, swiperRef]);

  return (
    <>
      {previewType === 'imageView_horizontal' && (
        <Swiper
          onSwiper={setSwiperRef}
          spaceBetween={12} // 썸네일 사이 간격
          slidesPerView="auto" // 썸네일이 고정 너비일 경우 유용
          freeMode={true} // 마우스 드래그로 스크롤 가능
          observer
          observeParents
          observeSlideChildren
          modules={[FreeMode]}
          style={{
            width: '100%',
            paddingBottom: '8px',
            paddingTop: '12px',
            cursor: disabled ? 'default' : 'grab',
            opacity: disabled ? 0.4 : 1,
          }}
        >
          {files.map((file: ExtendFile) => (
            <SwiperSlide
              key={file.id}
              style={{ width: 'auto' }} // 썸네일에 맞는 너비로 설정
            >
              <FileThumbnail
                previewType={previewType}
                file={file}
                onRemove={onRemove}
                disabled={disabled}
                loading={isFileUploading(file)}
                isDragging={false}
                tooltip
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      {previewType !== 'imageView_horizontal' && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              paddingTop: 2,
              alignItems: previewType === 'filename' ? 'flex-start' : 'center',
            }}
          >
            <SortableContext
              items={files as { id: UniqueIdentifier }[]}
              strategy={rectSortingStrategy}
              disabled={!sortable}
            >
              {files.map((file: ExtendFile) => (
                <SortableItem
                  key={file.id}
                  file={file}
                  previewType={previewType}
                  disabled={disabled}
                  loading={isFileUploading(file)}
                  onRemove={onRemove}
                />
              ))}
              {createPortal(
                <DragOverlay>
                  {activeFile && (
                    <FileThumbnail
                      key={activeFile.id}
                      file={activeFile}
                      previewType={previewType}
                      onRemove={onRemove}
                      disabled={disabled}
                      loading={isFileUploading(activeFile)}
                      isDragging={false}
                      tooltip
                    />
                  )}
                </DragOverlay>,
                document.querySelector('.MuiDialog-container') ??
                  document.querySelector('.MuiModal-root') ??
                  document.body,
              )}
              {useAddButton && (
                <IconButton
                  sx={{
                    width: imageSize,
                    height: imageSize,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    borderRadius: '6px',
                    backgroundImage: dashhed4,
                  }}
                  onClick={onFileOpen}
                  disabled={disabled || loading}
                >
                  <PlusIcon />
                </IconButton>
              )}
            </SortableContext>
          </Box>
        </DndContext>
      )}
    </>
  );
}

function SortableItem(props: FileThumbnailProps) {
  const { file, previewType, onRemove, disabled, loading } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
  };

  return (
    <Box ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <FileThumbnail
        key={file.id}
        previewType={previewType}
        file={file}
        onRemove={onRemove}
        disabled={disabled}
        loading={loading}
        isDragging={isDragging}
        tooltip
      />
    </Box>
  );
}
